import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Autocomplete,
  Box,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import { collection, onSnapshot, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { PRIORITIES } from './constants';
import { nowISO } from './helpers';

function formatContactLabel(c) {
  const nome = (c?.nome || '').trim();
  const azienda = (c?.azienda || '').trim();

  if (azienda && nome && azienda !== nome) {
    return `${azienda} — ${nome}`;
  }

  return azienda || nome || 'Contatto senza nome';
}

export default function TaskModal({ initial, onClose }) {
  const { staff } = useAuth();
  const [contatti, setContatti] = useState([]);
  const [saving, setSaving] = useState(false);
  const [contactQuery, setContactQuery] = useState('');

  const isAdmin = staff?.role === 'admin';

  const [f, setF] = useState({
  titolo: '',
  owner: staff?.nome || 'Mario',
  scadenza: new Date().toISOString().slice(0, 10),
  priority: 'media',
  fatto: false,
  contattoId: null,
  private: false,
  ...initial
});

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'crm_contatti'), s => {
      setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const up = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const selectedContact = useMemo(
    () => contatti.find(c => c.id === f.contattoId) || null,
    [contatti, f.contattoId]
  );

  useEffect(() => {
    if (selectedContact) {
      setContactQuery(formatContactLabel(selectedContact));
    }
  }, [selectedContact]);

  const filteredContatti = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();

    if (q.length < 2) return [];

    return contatti
      .filter(c => {
        const haystack = [
          c.nome || '',
          c.azienda || '',
          c.telefono || '',
          c.email || '',
          c.citta || ''
        ]
          .join(' ')
          .toLowerCase();

        return haystack.includes(q);
      })
      .sort((a, b) => formatContactLabel(a).localeCompare(formatContactLabel(b)))
      .slice(0, 12);
  }, [contatti, contactQuery]);

  const submit = async () => {
    if (!f.titolo?.trim()) return alert('Titolo obbligatorio');

    // Sicurezza: se non è admin, owner forzato a sé stessa
    const finalOwner = isAdmin ? f.owner : staff?.nome;

    setSaving(true);
    try {
      if (f.id) {
        const { id, ...data } = f;
        await updateDoc(doc(db, 'crm_task', id), { ...data, owner: finalOwner });
      } else {
        await addDoc(collection(db, 'crm_task'), { ...f, owner: finalOwner, createdAt: nowISO() });
      }
      onClose();
    } catch (err) {
      console.error('Errore salvataggio task:', err);
      alert('Errore durante il salvataggio del task.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {f.id ? 'Modifica task' : 'Nuovo task'}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Riga 1: Titolo */}
          <TextField
            fullWidth
            size="small"
            label="Titolo *"
            value={f.titolo}
            onChange={e => up('titolo', e.target.value)}
            autoFocus
          />

          {/* Riga 2: Scadenza + Priorità + Owner */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Scadenza"
              InputLabelProps={{ shrink: true }}
              value={f.scadenza}
              onChange={e => up('scadenza', e.target.value)}
            />

            <TextField
              select
              fullWidth
              size="small"
              label="Priorità"
              value={f.priority}
              onChange={e => up('priority', e.target.value)}
            >
              {PRIORITIES.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              size="small"
              label="Owner"
              value={f.owner}
              onChange={e => up('owner', e.target.value)}
              InputProps={{ readOnly: !isAdmin }}
              helperText={!isAdmin ? "Owner: te stessa" : ""}
            />
          </Box>

          {/* Riga 3: Contatto */}
          <Autocomplete
            fullWidth
            size="small"
            disablePortal
            options={filteredContatti}
            value={selectedContact}
            inputValue={contactQuery}
            onInputChange={(_, value, reason) => {
              setContactQuery(value);
              if (reason === 'clear') up('contattoId', null);
            }}
            onChange={(_, value) => {
              up('contattoId', value?.id || null);
              setContactQuery(value ? formatContactLabel(value) : '');
            }}
            getOptionLabel={(option) => formatContactLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={x => x}
            noOptionsText={contactQuery.trim().length < 2 ? 'Digita 2 lettere' : 'Nessun risultato'}
            ListboxProps={{ style: { maxHeight: 280 } }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id} sx={{ py: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {formatContactLabel(option)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {[option.telefono, option.citta].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Contatto (opzionale)"
                placeholder="Cerca azienda, nome o telefono..."
              />
            )}
          />

          {/* Riga 4: Privato */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!f.private}
                  onChange={e => up('private', e.target.checked)}
                />
              }
              label="Task privato (visibile solo a me)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
              Se disattivato, il task è visibile a tutti gli utenti staff.
            </Typography>
          </Box>

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={saving}>
          Annulla
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Salvataggio...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}